import { useLayoutEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '../auth/useAuth'
import { hubLembrado, lembrarHub, useHubLembrado, type HubKey } from '../hubs/hubs'
import { areaDoCaminho, hubDaArea, hubDoCaminho, type AreaKey } from './areas'
import { areasNavegaveis, hubsNavegaveis, ultimaArea } from './acesso'

/** As áreas que o usuário logado alcança, na ordem do catálogo, e a pergunta
 *  `temArea` para os atalhos que levam de uma área a outra. */
export function useAreas(): { areas: AreaKey[]; temArea: (area: AreaKey) => boolean } {
  const { user } = useAuth()
  return useMemo(() => {
    const areas = areasNavegaveis(user)
    return { areas, temArea: (area: AreaKey) => areas.includes(area) }
  }, [user])
}

/** O hub em que a pessoa está: o da aba, que é o da sessão em uso (03/11/2026). O
 *  endereço o define, por `useSincronizarHub`; em rota neutra, como
 *  `/conta/configuracoes`, ele fica onde estava. */
export function useHubAtual(): HubKey {
  return useHubLembrado()
}

/** Guarda o hub da rota aberta, para o tema, o `data-hub` do `<html>` e a próxima
 *  rota neutra acompanharem. Antes da pintura, para a tela não nascer com a cor do
 *  outro hub. Quem abre a rota de um hub que não alcança não muda nada: o portão
 *  da área o leva embora. */
export function useSincronizarHub(): void {
  const { pathname } = useLocation()
  const { user } = useAuth()
  const daRota = hubDoCaminho(pathname)
  const alcanca = !user || !user.is_staff || (daRota !== null && hubsNavegaveis(user).includes(daRota))
  useLayoutEffect(() => {
    if (daRota && alcanca && daRota !== hubLembrado()) lembrarHub(daRota)
  }, [daRota, alcanca])
}

/** A área cuja navegação a barra lateral mostra. Vem do endereço; em rota neutra,
 *  vale a última área visitada, se ainda for permitida, ou a primeira permitida do
 *  hub atual. */
export function useAreaAtual(): AreaKey | null {
  const { pathname } = useLocation()
  const { areas } = useAreas()
  const hub = useHubAtual()
  const doCaminho = areaDoCaminho(pathname)
  if (doCaminho) return areas.includes(doCaminho) ? doCaminho : null
  const ultima = ultimaArea()
  if (ultima && areas.includes(ultima) && hubDaArea(ultima) === hub) return ultima
  return areas.find((area) => hubDaArea(area) === hub) ?? areas[0] ?? null
}
